import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Trophy, Users, Calculator, ChevronDown, X, RefreshCw, Activity, DatabaseZap } from 'lucide-react';
import { useGames, useSeasonGames, useLeagues, useSeasons, useBaseballCache } from '../../../components/dashboards/hooks/useBaseballData';
import GameCardWithPrediction from './GameCardWithPrediction';
import StandingsTable from './StandingsTable';
import PlayerCard from './PlayerCard';
import SearchableDropdown from './SearchableDropdown';
import DatePicker from '../../../components/dashboards/components/shared/DatePicker';
import LoadingStates from './LoadingStates';
import BaseballEVCalculator from './BaseballEVCalculator';
import BaseballPredictionBar from './BaseballPredictionBar';
import MobileBaseballPredictionBar from './MobileBaseballPredictionBar';
import BaseballService from '../../../components/dashboards/services/baseballService';
import { useQuickPrediction } from '../../../components/dashboards/hooks/useQuickPrediction';
import { auth } from '../../../firebase';
import UpgradeNotice from '../../../components/upgradeNotice';
import StarCoinsModal from '../../../components/starCoinSystem/StarCoinsModal';
import BaseballDashMobile from './BaseballDashMobile';
import SeasonSeriesView from './SeasonSeriesView';
import UnifiedNavHeader from '../../../components/navigation/UnifiedNavHeader';
import internationalLeaguesData from './configs/international-baseball-leagues.json';

const BaseballDashRefactored = () => {
  // TODO: SEASON SERIES FEATURE FLAG
  // =====================================
  // Set to true when ready to enable Season Series analysis for all users
  // Current status: Feature complete but disabled for beta testing
  // To enable:
  //   1. Set SEASON_SERIES_ENABLED = true
  //   2. Remove "Coming Soon" overlay (lines 424-438)
  //   3. Enable team selection buttons (remove disabled/opacity/pointer-events-none)
  //   4. Test with production data before full release
  // =====================================
  const SEASON_SERIES_ENABLED = false;
  
  const [activeTab, setActiveTab] = useState('games');
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });

  // No default league - user must select from dropdown
  const [selectedLeague, setSelectedLeague] = useState(null);

  // Default to 2025 season (seasons are 4-digit years)
  const [selectedSeason, setSelectedSeason] = useState(2025);
  const [showOddsModal, setShowOddsModal] = useState(false);
  const [selectedGame, setSelectedGame] = useState(null);
  const [gamesPage, setGamesPage] = useState(1);
  const [playersPage, setPlayersPage] = useState(1);
  const [showStarCoinsModal, setShowStarCoinsModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedTeam, setSelectedTeam] = useState(null); // For series view

  const { leagues, loading: leaguesLoading } = useLeagues();
  const { seasons, loading: seasonsLoading } = useSeasons();
  const { cacheSize, clearCache, refreshCacheSize } = useBaseballCache();
  
  // Prediction hook with caching support
  const {
    isLoading: isPredicting,
    selectedGames,
    usageState,
    error: predictionError,
    showUpgradeNotice,
    toggleGameSelection,
    clearSelections,
    predictSelectedGames,
    setShowUpgradeNotice,
    getGamePrediction,
    isGameSelected,
    hasSelections,
    selectionCount,
    // NEW: Caching features
    frozenSelections,
    activeFilters,
    preserveFilterState,
    checkCachedPredictions,
    isGameFrozen
  } = useQuickPrediction();
  
  // No need for API testing on mount - we already know MLB is league ID 1
  
  // Fetch games from all priority leagues, then filter by selected league if any
  const [allGames, setAllGames] = useState([]);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [gamesError, setGamesError] = useState(null);

  const fetchAllLeaguesGames = useCallback(async () => {
    if (!selectedDate) return;

    setGamesLoading(true);
    setGamesError(null);

    try {
      // Get the 11 priority league IDs from JSON
      const internationalLeagueIds = (internationalLeaguesData.baseball_leagues || [])
        .map(league => league.leagueID)
        .filter(Boolean);

      const formattedDate = selectedDate instanceof Date
        ? selectedDate.toISOString().split('T')[0]
        : selectedDate;

      // Fetch games from all 11 leagues in parallel
      const gamesPromises = internationalLeagueIds.map(async (leagueId) => {
        try {
          const response = await BaseballService.getGames(
            null,
            formattedDate,
            leagueId,
            selectedSeason,
            null,
            'America/New_York'
          );
          return response.response || [];
        } catch (error) {
          // Silently handle individual league failures - some leagues may not have data
          return [];
        }
      });

      const allLeagueGames = await Promise.all(gamesPromises);
      const combined = allLeagueGames.flat();

      if (combined.length === 0) {
        console.log('No games found for selected date across all leagues');
      } else {
        console.log(`Fetched ${combined.length} games from all leagues`);
      }
      setAllGames(combined);
    } catch (error) {
      // Silently handle top-level errors - individual league failures are already handled
      setGamesError(error.message || 'Failed to fetch games');
      setAllGames([]);
    } finally {
      setGamesLoading(false);
    }
  }, [selectedDate, selectedSeason]);

  useEffect(() => {
    fetchAllLeaguesGames();
  }, [fetchAllLeaguesGames]);

  // Refetch function for manual refresh
  const refetchGames = useCallback(() => {
    fetchAllLeaguesGames();
  }, [fetchAllLeaguesGames]);

  // Filter games by selected league (or show all if no league selected)
  const games = useMemo(() => {
    if (!selectedLeague) {
      return allGames; // Show all games from all leagues
    }
    // Filter to only the selected league
    return allGames.filter(game => game.league?.id === selectedLeague.id);
  }, [allGames, selectedLeague]);

  // Fetch standings from all leagues or selected league
  const [allStandings, setAllStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [standingsError, setStandingsError] = useState(null);

  const fetchAllLeaguesStandings = useCallback(async () => {
    if (!selectedSeason) return;

    setStandingsLoading(true);
    setStandingsError(null);

    try {
      if (selectedLeague) {
        // Fetch standings for selected league only
        const response = await BaseballService.getStandings(
          selectedLeague.id,
          selectedSeason
        );
        setAllStandings(response.response || []);
      } else {
        // Fetch standings from all 11 leagues in parallel
        const internationalLeagueIds = (internationalLeaguesData.baseball_leagues || [])
          .map(league => league.leagueID)
          .filter(Boolean);

        const standingsPromises = internationalLeagueIds.map(async (leagueId) => {
          try {
            const response = await BaseballService.getStandings(
              leagueId,
              selectedSeason
            );
            return response.response || [];
          } catch (error) {
            // Silently handle individual league failures - some leagues may not have standings data
            return [];
          }
        });

        const allLeagueStandings = await Promise.all(standingsPromises);
        const combined = allLeagueStandings.flat();

        if (combined.length === 0) {
          console.log('No standings found for selected season across all leagues');
        } else {
          console.log(`Fetched standings from ${combined.length} divisions/groups across all leagues`);
        }
        setAllStandings(combined);
      }
    } catch (error) {
      // Silently handle top-level errors - individual league failures are already handled
      setStandingsError(error.message || 'Failed to fetch standings');
      setAllStandings([]);
    } finally {
      setStandingsLoading(false);
    }
  }, [selectedLeague, selectedSeason]);

  useEffect(() => {
    fetchAllLeaguesStandings();
  }, [fetchAllLeaguesStandings]);

  const refetchStandings = useCallback(() => {
    fetchAllLeaguesStandings();
  }, [fetchAllLeaguesStandings]);

  // Use the fetched standings
  const standings = allStandings;

  // Fetch all season games for series view (only when on series tab)
  const {
    games: seasonGames,
    loading: seasonGamesLoading,
    progress: seasonGamesProgress
  } = useSeasonGames({
    league: selectedLeague?.id,
    season: selectedSeason,
    teamId: selectedTeam?.id,
    enabled: activeTab === 'series' && selectedTeam !== null
  });

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    setGamesPage(1);
    setPlayersPage(1);
  }, []);

  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
    setGamesPage(1);
  }, []);

  const handleLeagueChange = useCallback((league) => {
    setSelectedLeague(league);
    setGamesPage(1);
    setPlayersPage(1);
  }, []);

  const handleSeasonChange = useCallback((season) => {
    setSelectedSeason(season);
  }, []);

  // No auto-select - user chooses league manually from dropdown

  const handleShowOdds = useCallback(async (game) => {
    setSelectedGame(game);
    setShowOddsModal(true);
    
    // Try to fetch odds for this game
    try {
      const oddsData = await BaseballService.getOdds(null, null, game.id);
      // console.log('Odds response:', oddsData); // uncomment this for testing
      
      if (oddsData.response && oddsData.response.length > 0 && oddsData.response[0].bookmakers) {
        // Extract bookmakers array from the response
        const bookmakers = oddsData.response[0].bookmakers;
        // console.log('Extracted bookmakers:', bookmakers); // uncomment this for testing
        
        setSelectedGame({ ...game, oddsData: bookmakers });
      }
    } catch (error) {
      // console.log('No odds available for game:', game.id); // uncomment this for testing
    }
  }, []);

  const handleClearCache = useCallback(async () => {
    clearCache();
    await Promise.all([
      refetchGames(),
      refetchStandings()
    ]);
  }, [clearCache, refetchGames, refetchStandings]);
  
  // Check for cached predictions when games change
  useEffect(() => {
    const checkCached = async () => {
      if (games && games.length > 0 && activeTab === 'games') {
        const gameIds = games.map(game => game.id).filter(Boolean);
        if (gameIds.length > 0) {
          const cachedPreds = await checkCachedPredictions('baseball', gameIds);
          if (Object.keys(cachedPreds).length > 0) {
            console.log(`[BaseballDashRefactored] Loaded ${Object.keys(cachedPreds).length} cached predictions`);
          }
        }
      }
    };
    checkCached();
  }, [games, activeTab, checkCachedPredictions]);
  
  // Listen for star coins modal event
  useEffect(() => {
    const handleOpenStarCoinsModal = () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setShowStarCoinsModal(true);
    };
    
    window.addEventListener('openStarCoinsModal', handleOpenStarCoinsModal);
    return () => {
      window.removeEventListener('openStarCoinsModal', handleOpenStarCoinsModal);
    };
  }, []);
  
  // Mobile resize detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const formatCacheSize = useCallback((bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }, []);

  const gamesPerPage = 12;

  // Sort games by status: live first, then upcoming, then finished
  const sortedGames = useMemo(() => {
    if (!games || games.length === 0) return [];
    
    return [...games].sort((a, b) => {
      const statusA = a.status?.short;
      const statusB = b.status?.short;
      
      // Check if games are live (baseball uses IN1-IN9 for innings)
      const aIsLive = statusA && (
        statusA.startsWith('IN') || 
        statusA === 'LIVE' || 
        statusA === 'IN_PLAY' ||
        ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(statusA)
      );
      const bIsLive = statusB && (
        statusB.startsWith('IN') || 
        statusB === 'LIVE' || 
        statusB === 'IN_PLAY' ||
        ['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(statusB)
      );
      
      // Check if games are finished
      const aIsFinished = statusA && (statusA === 'FT' || statusA === 'AOT' || statusA === 'FINISHED');
      const bIsFinished = statusB && (statusB === 'FT' || statusB === 'AOT' || statusB === 'FINISHED');
      
      // Priority order: Live > Upcoming (NS) > Finished
      if (aIsLive && !bIsLive) return -1;
      if (!aIsLive && bIsLive) return 1;
      
      // Both not live - check if one is finished
      if (!aIsLive && !bIsLive) {
        if (!aIsFinished && bIsFinished) return -1;
        if (aIsFinished && !bIsFinished) return 1;
      }
      
      // Same status category - sort by time for upcoming games
      if (!aIsLive && !aIsFinished && !bIsLive && !bIsFinished) {
        return new Date(a.date) - new Date(b.date);
      }
      
      return 0;
    });
  }, [games]);

  const paginatedGames = useMemo(() => {
    const startIndex = (gamesPage - 1) * gamesPerPage;
    return sortedGames.slice(startIndex, startIndex + gamesPerPage);
  }, [sortedGames, gamesPage]);

  const totalGamesPages = Math.ceil(games.length / gamesPerPage);

  const tabs = [
    { id: 'games', label: 'Games', icon: Calendar },
    { id: 'series', label: 'Season Series', icon: Activity },
    { id: 'standings', label: 'Standings', icon: Trophy },
    { id: 'players', label: 'Players', icon: Users },
    { id: 'calculator', label: 'EV Calculator', icon: Calculator },
  ];

  const leagueOptions = useMemo(() => {
    // ONLY show leagues from international-baseball-leagues.json
    const internationalLeagueIds = (internationalLeaguesData.baseball_leagues || [])
      .map(league => league.leagueID)
      .filter(Boolean);

    return leagues
      .filter(league => internationalLeagueIds.includes(league.id))
      .map(league => ({
        id: league.id,
        name: league.name,
        logo: league.logo
      }));
  }, [leagues]);

  const seasonOptions = useMemo(() => 
    seasons.map(season => ({
      id: season,
      name: season.toString()
    })), [seasons]
  );

  // Render mobile component on mobile devices
  if (isMobile) {
    return <BaseballDashMobile />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
      {/* Add Unified Navigation Header */}
      <UnifiedNavHeader 
        title="Baseball Dashboard"
        showBackButton={true}
      />
      
      <div className="p-4 pb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
              Baseball Dashboard
            </h1>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-400">
                Cache: {formatCacheSize(cacheSize)}
              </div>
              <button
                onClick={handleClearCache}
                className="flex items-center gap-2 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Clear Cache
              </button>
            </div>
          </div>

          <div className="flex gap-2 mb-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg'
                    : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
            <p className="text-sm text-blue-200 flex items-center gap-1">
              <strong>All AI picks saved to chat history</strong>
              <DatabaseZap className="w-4 h-4 inline" />
              <span>Go to chat to dive deeper • v3-Beta: Not all features available yet</span>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {activeTab === 'games' && (
              <>
                <DatePicker
                  selectedDate={selectedDate}
                  onChange={handleDateChange}
                  className="lg:col-span-2"
                />
                <button
                  onClick={() => refetchGames()}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh Live Scores
                </button>
              </>
            )}
            
            <SearchableDropdown
              options={leagueOptions}
              value={selectedLeague}
              onChange={handleLeagueChange}
              placeholder="All Leagues"
              loading={leaguesLoading}
              renderOption={(option) => (
                <div className="flex items-center gap-2">
                  {option.logo && (
                    <img src={option.logo} alt={option.name} className="w-6 h-6" />
                  )}
                  <span>{option.name}</span>
                </div>
              )}
            />

            {(activeTab === 'standings' || activeTab === 'players') && (
              <SearchableDropdown
                options={seasonOptions}
                value={seasonOptions.find(s => s.id === selectedSeason)}
                onChange={(season) => handleSeasonChange(season?.id)}
                placeholder="Select Season"
                loading={seasonsLoading}
              />
            )}
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {activeTab === 'games' && (
            <motion.div
              key="games"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {gamesLoading ? (
                <LoadingStates.GameGrid />
              ) : gamesError ? (
                <div className="text-center py-8 text-red-400">
                  <p>Error loading games: {gamesError}</p>
                  <button
                    onClick={refetchGames}
                    className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : games.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p>No games found for the selected date and league</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {paginatedGames.map((game) => (
                      <GameCardWithPrediction
                        key={game.id}
                        game={game}
                        onShowOdds={handleShowOdds}
                        isSelected={isGameSelected(game)}
                        isFrozen={isGameFrozen(game.id)}
                        onToggleSelection={toggleGameSelection}
                        prediction={getGamePrediction(game.id)}
                        showPredictionButton={true}
                      />
                    ))}
                  </div>
                  
                  {totalGamesPages > 1 && (
                    <div className="flex justify-center gap-2 mt-6">
                      {Array.from({ length: totalGamesPages }, (_, i) => i + 1).map((page) => (
                        <button
                          key={page}
                          onClick={() => setGamesPage(page)}
                          className={`px-3 py-1 rounded ${
                            gamesPage === page
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                          }`}
                        >
                          {page}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'standings' && (
            <motion.div
              key="standings"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {standingsLoading ? (
                <LoadingStates.StandingsTable />
              ) : standingsError ? (
                <div className="text-center py-8 text-red-400">
                  <p>Error loading standings: {standingsError}</p>
                  <button
                    onClick={refetchStandings}
                    className="mt-4 px-4 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : standings.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <p>No standings available for the selected league and season</p>
                </div>
              ) : (
                <StandingsTable standings={standings} />
              )}
            </motion.div>
          )}

          {activeTab === 'players' && (
            <motion.div
              key="players"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <div className="text-center py-8 text-gray-400">
                <p>Player statistics coming soon</p>
              </div>
            </motion.div>
          )}

          {activeTab === 'series' && (
            <motion.div
              key="series"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {selectedTeam && SEASON_SERIES_ENABLED ? (
                <SeasonSeriesView 
                  teamId={selectedTeam.id}
                  season={selectedSeason}
                  games={seasonGames || []}
                  standings={standings}
                  loading={seasonGamesLoading}
                  progress={seasonGamesProgress}
                />
              ) : (
                <div className="bg-white rounded-xl shadow-lg p-8 relative">
                  {/* TODO: REMOVE THIS OVERLAY WHEN ENABLING SEASON SERIES */}
                  {/* Coming Soon Overlay - Remove lines 433-447 when feature flag is enabled */}
                  <div className="absolute inset-0 bg-white/90 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                    <div className="text-center p-8">
                      <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full mb-4">
                        <Activity className="w-8 h-8 text-emerald-600" />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">Coming Soon!</h2>
                      <p className="text-gray-600 max-w-md mx-auto">
                        Season Series analysis with opponent momentum tracking and ranking trajectories will be available soon.
                      </p>
                      <div className="mt-4 inline-flex items-center px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
                        <span className="mr-2">🚀</span>
                        Feature in Beta Testing
                      </div>
                    </div>
                  </div>
                  
                  {/* TODO: RE-ENABLE WHEN FEATURE FLAG IS TRUE */}
                  {/* Original content - Remove opacity/pointer-events-none/disabled when enabling */}
                  <h3 className="text-lg font-semibold mb-4 opacity-50">Select a Team to View Season Series</h3>
                  <p className="text-gray-600 mb-6 opacity-50">
                    Choose a team to visualize their season series-by-series with opponent momentum and ranking trends.
                  </p>
                  {/* TODO: Remove opacity-30, pointer-events-none, disabled, cursor-not-allowed, and grayscale when enabling */}
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 opacity-30 pointer-events-none">
                    {BaseballService.getMLBTeams().map(team => (
                      <button
                        key={team.id}
                        onClick={() => setSelectedTeam(team)}
                        className="p-4 rounded-lg border-2 border-gray-200 transition-all cursor-not-allowed"
                        disabled={true}
                      >
                        <img 
                          src={team.logo} 
                          alt={team.name}
                          className="w-12 h-12 mx-auto mb-2 grayscale"
                        />
                        <p className="text-xs font-medium text-center">{team.abbreviation}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'calculator' && (
            <motion.div
              key="calculator"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <BaseballEVCalculator />
            </motion.div>
          )}
        </AnimatePresence>

        {showOddsModal && selectedGame && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowOddsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-800 rounded-xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold">
                  EV Calculator - {selectedGame.teams?.away?.name} @ {selectedGame.teams?.home?.name}
                </h2>
                <button
                  onClick={() => setShowOddsModal(false)}
                  className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <BaseballEVCalculator prefilledGame={selectedGame} />
            </motion.div>
          </motion.div>
        )}
      </div>
      
      {/* Prediction Control Bar */}
      {isMobile ? (
        <MobileBaseballPredictionBar
          selectionCount={selectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          error={predictionError}
          onPredict={() => {
            // Preserve current dashboard state
            preserveFilterState({
              date: selectedDate,
              league: selectedLeague,
              season: selectedSeason,
              tab: activeTab
            });
            // Then trigger prediction
            predictSelectedGames('baseball');
          }}
          onClear={clearSelections}
        />
      ) : (
        <BaseballPredictionBar
          selectionCount={selectionCount}
          usageState={usageState}
          isLoading={isPredicting}
          error={predictionError}
          onPredict={() => {
            // Preserve current dashboard state
            preserveFilterState({
              date: selectedDate,
              league: selectedLeague,
              season: selectedSeason,
              tab: activeTab
            });
            // Then trigger prediction
            predictSelectedGames('baseball');
          }}
          onClear={clearSelections}
        />
      )}
      
      {/* Upgrade Notice when out of predictions */}
      {showUpgradeNotice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <UpgradeNotice
              hoursUntilReset={usageState.hoursUntilReset}
              onClose={() => setShowUpgradeNotice(false)}
              isMobile={false}
              tier={auth.currentUser?.tier || 'basic'}
              messageCount={usageState.remaining}
              dailyLimit={usageState.remaining === 0 ? 1 : 7}
            />
          </div>
        </div>
      )}
      
      {/* Star Coins Modal - Desktop Optimized */}
      {showStarCoinsModal && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, type: "spring", damping: 30 }}
            className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl border border-white/10"
          >
            <StarCoinsModal
              isOpen={true}
              onClose={() => setShowStarCoinsModal(false)}
              userId={auth.currentUser?.uid}
            />
          </motion.div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BaseballDashRefactored;